import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Dropdown } from 'react-bootstrap'
import { TooltipComponent } from '@syncfusion/ej2-react-popups'
import moment from 'moment'
import type { RootState } from '@store/index'
import Button from '@components/common/Button/Button.component'
import DashboardSectionCard from '@components/pages/Dashboard/DashboardSectionCard.component'
import DashboardSectionCardSkeleton from '@components/pages/Dashboard/DashboardSectionCardSkeleton.component'
import DashboardSectionEmptyState from '@components/pages/Dashboard/DashboardSectionEmptyState.component'
import { DashboardMetricTileGrid } from '@components/pages/Dashboard/Shared/DashboardMetricTileGrid.component'
import { OverviewTodaySchedulePanel } from '@components/pages/Dashboard/Overview/OverviewTodaySchedulePanel.component'
import { useDashboardRefreshKey } from '@context/DashboardRefreshContext'
import type { AppointmentResponse } from '@dataTypes/appointment.type'
import type { RangeKey } from '@dataTypes/dashboard.types'
import type { DashboardUtilizationData } from '@hooks/dashboard/useDashboardUtilizationData'
import { clinicIdsFromProvider } from '@utils/staffClinicScope.util'
import { mapSchedulingOverviewToPrimaryMetrics } from '@utils/overviewAppointments.util'
import {
  CALENDAR_DATE_FORMAT,
  rangeToPeriod,
} from '@utils/dashboardFinance.util'
import {
  isDashboardDevMocksEnabled,
  withDashboardMetricShellValues,
} from '@utils/dashboardDevMocks.util'
import {
  hasNonZeroDashboardValues,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import { getOverviewAppointmentsMetrics } from '../overviewMetrics.mockData'

type AppointmentsCardProps = {
  date: string
  range: RangeKey
  showAppointments: boolean
  showTodaySchedule: boolean
  schedulingOverview: DashboardUtilizationData['schedulingOverview']
  schedulingOverviewDataParams: DashboardUtilizationData['schedulingOverviewDataParams']
  schedulingLoading: boolean
  appointmentSecondaryMetrics: DashboardUtilizationData['appointmentSecondaryMetrics']
  operatoriesSettled: boolean
  todayScheduleAppointments: AppointmentResponse[] | null
  appointmentFetchLoading: boolean
}

export function AppointmentsCard({
  date,
  range,
  showAppointments,
  showTodaySchedule,
  schedulingOverview,
  schedulingOverviewDataParams,
  schedulingLoading,
  appointmentSecondaryMetrics,
  operatoriesSettled,
  todayScheduleAppointments,
  appointmentFetchLoading,
}: AppointmentsCardProps): React.ReactElement {
  const useMocks = isDashboardDevMocksEnabled()
  const providersFromStore = useSelector(
    (state: RootState) => state.provider.data,
  )
  const selectedClinicId = useSelector(
    (state: RootState) => state.clinic.selectedClinic?.clinic_id,
  )
  const operatoryLoading = useSelector(
    (state: RootState) => state.operatory.loading,
  )
  const providerList = useMemo(() => {
    const providers = providersFromStore ?? []
    if (!selectedClinicId) return providers
    return providers.filter((provider) =>
      clinicIdsFromProvider(provider).includes(selectedClinicId),
    )
  }, [providersFromStore, selectedClinicId])
  const [todayScheduleProviderKey, setTodayScheduleProviderKey] =
    useState('all')

  const todayScheduleProviderOptions = useMemo(() => {
    const opts: { key: string; label: string }[] = [
      { key: 'all', label: 'All providers' },
    ]
    for (const provider of providerList) {
      const label = [provider.first_name, provider.last_name]
        .filter(Boolean)
        .join(' ')
      if (label) opts.push({ key: provider.provider_id, label })
    }
    return opts
  }, [providerList])

  const todayScheduleProviderLabel =
    todayScheduleProviderOptions.find(
      (opt) => opt.key === todayScheduleProviderKey,
    )?.label ?? 'All providers'

  const displayAppointmentList = useMemo(() => {
    const list = todayScheduleAppointments ?? []
    if (todayScheduleProviderKey === 'all') return list
    return list.filter((appt) => appt.provider_id === todayScheduleProviderKey)
  }, [todayScheduleAppointments, todayScheduleProviderKey])

  const mockAppointmentsMetrics = useMemo(
    () =>
      withDashboardMetricShellValues(
        getOverviewAppointmentsMetrics(range),
        useMocks,
      ),
    [range, useMocks],
  )
  const appointmentsMetrics = useMemo(() => {
    if (!schedulingOverview) return mockAppointmentsMetrics
    return mapSchedulingOverviewToPrimaryMetrics(
      schedulingOverview,
      range,
      mockAppointmentsMetrics,
    )
  }, [schedulingOverview, range, mockAppointmentsMetrics])

  const appointmentsScheduleHref = useMemo(() => {
    const query = new URLSearchParams({
      range,
      date: moment(date, 'MMMM D, YYYY').format('YYYY-MM-DD'),
    })
    return `/dashboard/schedule?${query.toString()}`
  }, [date, range])

  // Open chair minutes are capacity, not appointment activity. Without a
  // response, fall back to the effective rendered metrics (blank shells when
  // mocks are off, mock values when on).
  const appointmentsMetricsHaveData = schedulingOverview
    ? hasNonZeroDashboardValues(
        schedulingOverview.summary.scheduled.current,
        schedulingOverview.summary.confirmationRate.current,
        schedulingOverview.summary.noShowRate.current,
        schedulingOverview.summary.newPatients.current,
        schedulingOverview.summary.hygieneUtilizationRate.current,
        schedulingOverview.summary.sameDayTreatmentRate.current,
        schedulingOverview.summary.pendingTreatmentValue.current,
      )
    : hasNonZeroDashboardValues(
        ...appointmentsMetrics.map((metric) => metric.value),
        ...appointmentSecondaryMetrics.map((metric) => metric.value),
      )
  const todayScheduleHasAppointments = displayAppointmentList.length > 0

  const [appointmentsFetchSettledForDate, setAppointmentsFetchSettledForDate] =
    useState(!showTodaySchedule)
  const [schedulingMetricsSettled, setSchedulingMetricsSettled] = useState(
    () => !showAppointments || schedulingOverview != null,
  )
  const appointmentsFetchSawLoadingRef = useRef(false)
  const schedulingMetricsSawLoadingRef = useRef(false)
  const appointmentsFetchDateRef = useRef(date)
  const schedulingMetricsPeriodRef = useRef(`${date}:${range}`)
  const refreshKey = useDashboardRefreshKey()
  const [appointmentsRefreshPending, setAppointmentsRefreshPending] =
    useState(false)
  const refreshKeyRef = useRef(refreshKey)
  const refreshSawSchedulingLoadingRef = useRef(false)
  const refreshSawAppointmentLoadingRef = useRef(false)

  useEffect(() => {
    if (appointmentsFetchDateRef.current === date) return
    appointmentsFetchDateRef.current = date
    appointmentsFetchSawLoadingRef.current = false
    setAppointmentsFetchSettledForDate(!showTodaySchedule)
  }, [date, showTodaySchedule])
  useEffect(() => {
    const periodKey = `${date}:${range}`
    if (schedulingMetricsPeriodRef.current === periodKey) return
    schedulingMetricsPeriodRef.current = periodKey
    schedulingMetricsSawLoadingRef.current = false
    setSchedulingMetricsSettled(!showAppointments)
  }, [date, range, showAppointments])
  useEffect(() => {
    if (!showTodaySchedule) {
      setAppointmentsFetchSettledForDate(true)
      return
    }
    if (appointmentFetchLoading) {
      appointmentsFetchSawLoadingRef.current = true
      return
    }
    // Cache restore never flips loading to true, so a loaded list (including
    // an empty one) must settle on its own. A null list with loading still
    // false is the idle frame before the request starts.
    if (
      appointmentsFetchSawLoadingRef.current ||
      todayScheduleAppointments != null
    ) {
      setAppointmentsFetchSettledForDate(true)
    }
  }, [appointmentFetchLoading, showTodaySchedule, todayScheduleAppointments])

  const schedulingOverviewMatchesPeriod = useMemo(() => {
    if (schedulingOverviewDataParams == null) return false
    const periodDate = moment(date, CALENDAR_DATE_FORMAT, true).format(
      'YYYY-MM-DD',
    )
    return (
      schedulingOverviewDataParams.date === periodDate &&
      schedulingOverviewDataParams.period === rangeToPeriod(range)
    )
  }, [schedulingOverviewDataParams, date, range])

  useEffect(() => {
    if (!showAppointments) {
      setSchedulingMetricsSettled(true)
      return
    }
    if (schedulingLoading) {
      schedulingMetricsSawLoadingRef.current = true
      return
    }
    if (schedulingOverviewMatchesPeriod) {
      setSchedulingMetricsSettled(true)
      return
    }
    if (operatoriesSettled && schedulingMetricsSawLoadingRef.current) {
      setSchedulingMetricsSettled(true)
    }
  }, [
    schedulingOverviewMatchesPeriod,
    schedulingLoading,
    showAppointments,
    operatoriesSettled,
  ])
  useEffect(() => {
    if (refreshKeyRef.current === refreshKey) return
    refreshKeyRef.current = refreshKey
    if (refreshKey === 0) return
    refreshSawSchedulingLoadingRef.current = false
    refreshSawAppointmentLoadingRef.current = false
    setAppointmentsRefreshPending(true)
  }, [refreshKey])
  useEffect(() => {
    if (!appointmentsRefreshPending) return

    if (showAppointments && schedulingLoading) {
      refreshSawSchedulingLoadingRef.current = true
    }
    if (showTodaySchedule && appointmentFetchLoading) {
      refreshSawAppointmentLoadingRef.current = true
    }

    const schedulingDone =
      !showAppointments ||
      (refreshSawSchedulingLoadingRef.current && !schedulingLoading)
    const appointmentsDone =
      !showTodaySchedule ||
      (refreshSawAppointmentLoadingRef.current && !appointmentFetchLoading)

    if (schedulingDone && appointmentsDone) {
      setAppointmentsRefreshPending(false)
      return
    }

    const allIdle =
      (!showAppointments || !schedulingLoading) &&
      (!showTodaySchedule || !appointmentFetchLoading)
    if (!allIdle) return

    const timeoutId = window.setTimeout(() => {
      setAppointmentsRefreshPending(false)
    }, 0)
    return () => window.clearTimeout(timeoutId)
  }, [
    appointmentsRefreshPending,
    showAppointments,
    showTodaySchedule,
    schedulingLoading,
    appointmentFetchLoading,
  ])

  const todayScheduleEmpty = shouldShowDashboardSectionEmpty(
    appointmentFetchLoading ||
      operatoryLoading ||
      !operatoriesSettled ||
      !appointmentsFetchSettledForDate,
    todayScheduleHasAppointments,
  )
  const appointmentsSectionEmpty =
    operatoriesSettled &&
    appointmentsFetchSettledForDate &&
    schedulingMetricsSettled &&
    !todayScheduleHasAppointments &&
    !(showAppointments && appointmentsMetricsHaveData)
  const appointmentsHostPending =
    !operatoriesSettled ||
    operatoryLoading ||
    appointmentsRefreshPending ||
    (showAppointments && !schedulingMetricsSettled) ||
    (showTodaySchedule && !appointmentsFetchSettledForDate)
  const appointmentsHostSkeleton = showAppointments
    ? ({ metrics: 8, metricColumns: 4, block: 'tall' } as const)
    : ({ calendar: true, block: 'tall' } as const)

  const todayScheduleFilters = (
    <div className="dashboard-todays-schedule-filters">
      <Dropdown className="dashboard-section-filter-dropdown">
        <Dropdown.Toggle
          as={Button}
          variant=""
          className="button-secondary button-sm dashboard-section-filter-dropdown-toggle"
        >
          <span>{todayScheduleProviderLabel}</span>
          <i className="mgc mgc_down_small_fill" aria-hidden />
        </Dropdown.Toggle>
        <Dropdown.Menu>
          {todayScheduleProviderOptions.map((opt) => (
            <Dropdown.Item
              key={opt.key}
              onClick={() => setTodayScheduleProviderKey(opt.key)}
            >
              {opt.label}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
      <TooltipComponent
        content="Today's schedule information"
        opensOn="Hover"
        cssClass="e-tooltip-template-css e-tooltip-tab-feature"
        position="TopCenter"
      >
        <button
          type="button"
          className="dashboard-todays-schedule-info-btn"
          aria-label="Today's schedule information"
        >
          <i className="mgc mgc_information_fill" aria-hidden />
        </button>
      </TooltipComponent>
    </div>
  )

  return (
    <DashboardSectionCard
      title="Appointments"
      titleHref={appointmentsScheduleHref}
      className="dashboard-overview-appointments-card"
      disableTitleLink={appointmentsSectionEmpty}
      headerRight={
        showTodaySchedule && !appointmentsHostPending && !todayScheduleEmpty
          ? todayScheduleFilters
          : undefined
      }
    >
      <div
        className={[
          'dashboard-overview-appointments-body',
          appointmentsHostPending
            ? 'dashboard-overview-appointments-body--pending'
            : '',
          !appointmentsHostPending && appointmentsSectionEmpty
            ? 'dashboard-overview-appointments-body--empty'
            : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {showAppointments && (
          <div className="dashboard-overview-appointments-metrics">
            <DashboardMetricTileGrid
              columns={4}
              metrics={appointmentsMetrics}
            />
            <DashboardMetricTileGrid
              columns={4}
              metrics={appointmentSecondaryMetrics}
            />
          </div>
        )}
        {showTodaySchedule ? (
          <OverviewTodaySchedulePanel
            date={date}
            providerKey={todayScheduleProviderKey}
            enabled={showTodaySchedule}
            appointments={todayScheduleAppointments}
          />
        ) : null}
        {appointmentsHostPending ? (
          <div className="dashboard-overview-appointments-overlay" aria-hidden>
            <DashboardSectionCardSkeleton {...appointmentsHostSkeleton} />
          </div>
        ) : null}
        {!appointmentsHostPending && appointmentsSectionEmpty ? (
          <div className="dashboard-overview-appointments-overlay">
            <DashboardSectionEmptyState />
          </div>
        ) : null}
      </div>
    </DashboardSectionCard>
  )
}
